import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VipRestriction from '../components/VipRestriction';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Space,
  Tag,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Statistic,
  Steps,
  Timeline,
  Tabs,
  List,
  Avatar,
  Divider
} from 'antd';
import {
  EnvironmentOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  HomeOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  EyeOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  MessageOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined as CalendarIcon,
  FileTextOutlined as FileIcon,
  HeartOutlined as HeartIcon,
  MessageOutlined as MessageIcon,
  EnvironmentOutlined as EnvIcon
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { environmentConfig } from '../config/environment';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { TabPane } = Tabs;

const Environment = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [environments, setEnvironments] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [activeTab, setActiveTab] = useState('environments');
  const [environmentForm] = Form.useForm();
  const [followUpForm] = Form.useForm();

  // 获取环境管理列表
  const fetchEnvironments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(environmentConfig.apiEndpoints.environments);
      if (response.data.success) {
        setEnvironments(response.data.records || []);
      } else {
        message.error(response.data.message || '获取环境管理列表失败');
      }
    } catch (error) {
      console.error('获取环境管理列表失败:', error);
      message.error('获取环境管理列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取跟进记录
  const fetchFollowUps = async () => {
    try {
      const response = await axios.get(environmentConfig.apiEndpoints.followUps);
      if (response.data.success) {
        setFollowUps(response.data.followUps || []);
      }
    } catch (error) {
      console.error('获取跟进记录失败:', error);
    }
  };

  // 打开环境管理弹窗
  const handleOpenEnvironment = (environment = null) => {
    setSelectedEnvironment(environment);
    if (environment) {
      environmentForm.setFieldsValue({
        ...environment,
        created_date: environment.created_date ? dayjs(environment.created_date) : null,
        target_date: environment.target_date ? dayjs(environment.target_date) : null
      });
    } else {
      environmentForm.resetFields();
    }
    setShowEnvironmentModal(true);
  };

  // 打开跟进记录弹窗
  const handleOpenFollowUp = (followUp = null, environmentId = null) => {
    setSelectedFollowUp(followUp);
    if (followUp) {
      followUpForm.setFieldsValue({
        ...followUp,
        follow_up_date: followUp.follow_up_date ? dayjs(followUp.follow_up_date) : null
      });
    } else {
      followUpForm.resetFields();
      if (environmentId) {
        followUpForm.setFieldsValue({ environment_id: environmentId });
      }
    }
    setShowFollowUpModal(true);
  };

  // 保存环境管理
  const handleSaveEnvironment = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values,
        created_date: values.created_date ? (dayjs.isDayjs(values.created_date) ? values.created_date.format('YYYY-MM-DD') : values.created_date) : null,
        target_date: values.target_date ? (dayjs.isDayjs(values.target_date) ? values.target_date.format('YYYY-MM-DD') : values.target_date) : null
      };

      let response;
      if (selectedEnvironment) {
        response = await axios.put(`${environmentConfig.apiEndpoints.updateEnvironment}/${selectedEnvironment.id}`, requestBody);
      } else {
        response = await axios.post(environmentConfig.apiEndpoints.createEnvironment, requestBody);
      }

      if (response.data.success) {
        message.success(selectedEnvironment ? '更新成功' : '创建成功');
        setShowEnvironmentModal(false);
        fetchEnvironments();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存环境管理失败:', error);
      message.error('操作失败');
    }
  };

  // 保存跟进记录
  const handleSaveFollowUp = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values,
        follow_up_date: values.follow_up_date ? (dayjs.isDayjs(values.follow_up_date) ? values.follow_up_date.format('YYYY-MM-DD') : values.follow_up_date) : null
      };

      let response;
      if (selectedFollowUp) {
        response = await axios.put(`${environmentConfig.apiEndpoints.updateFollowUp}/${selectedFollowUp.id}`, requestBody);
      } else {
        response = await axios.post(environmentConfig.apiEndpoints.createFollowUp, requestBody);
      }

      if (response.data.success) {
        message.success(selectedFollowUp ? '更新成功' : '创建成功');
        setShowFollowUpModal(false);
        fetchFollowUps();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存跟进记录失败:', error);
      message.error('操作失败');
    }
  };

  // 删除环境管理
  const handleDeleteEnvironment = async (id) => {
    try {
      const response = await axios.delete(`${environmentConfig.apiEndpoints.deleteEnvironment}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchEnvironments();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除环境管理失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = environmentConfig.environmentTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = environmentConfig.environmentTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = environmentConfig.environmentTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    const statusObj = environmentConfig.implementationStatus.find(s => s.key === status);
    return statusObj ? statusObj.color : 'default';
  };

  // 获取状态名称
  const getStatusName = (status) => {
    const statusObj = environmentConfig.implementationStatus.find(s => s.key === status);
    return statusObj ? statusObj.name : '未知';
  };

  // 获取优先级颜色
  const getPriorityColor = (priority) => {
    const priorityObj = environmentConfig.priorities.find(p => p.key === priority);
    return priorityObj ? priorityObj.color : 'default';
  };

  // 获取优先级名称
  const getPriorityName = (priority) => {
    const priorityObj = environmentConfig.priorities.find(p => p.key === priority);
    return priorityObj ? priorityObj.name : '未知';
  };

  // 计算统计信息
  const totalEnvironments = environments.length;
  const completedEnvironments = environments.filter(env => env.status === 'completed').length;
  const inProgressEnvironments = environments.filter(env => env.status === 'in_progress').length;
  const completionRate = totalEnvironments > 0 ? Math.round((completedEnvironments / totalEnvironments) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchEnvironments();
    fetchFollowUps();
  }, [initialize]);

  return (
    <VipRestriction>
      <div className="mod-page">
      {/* 头部导航 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <EnvironmentOutlined style={{ marginRight: '8px', color: '#6366F1' }} />
            {environmentConfig.name}
          </div>
          <div className="mod-header-subtitle">
            规划与跟进你的家居环境改善项目，营造健康、舒适、可持续的生活空间
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
          >
            环境TIPS
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenEnvironment()}
          >
            新增环境管理
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon"><EnvironmentOutlined style={{ color: '#6366F1' }} /></div>
          <div>
            <div className="mod-stat-value">{totalEnvironments}</div>
            <div className="mod-stat-label">全部项目</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon"><ClockCircleOutlined style={{ color: '#F59E0B' }} /></div>
          <div>
            <div className="mod-stat-value">{inProgressEnvironments}</div>
            <div className="mod-stat-label">进行中</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon"><CheckCircleOutlined style={{ color: '#22C55E' }} /></div>
          <div>
            <div className="mod-stat-value">{completedEnvironments}</div>
            <div className="mod-stat-label">已完成</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon"><BarChartOutlined style={{ color: '#6366F1' }} /></div>
          <div>
            <div className="mod-stat-value">{completionRate}%</div>
            <div className="mod-stat-label">完成率</div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div>
        <Row gutter={[16, 16]}>
            {environments.map((environment) => (
              <Col xs={24} sm={12} md={8} lg={6} key={environment.id}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '6px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  actions={[
                    <EditOutlined key="edit" onClick={() => handleOpenEnvironment(environment)} />,
                    <PlusOutlined key="add" onClick={() => handleOpenFollowUp(null, environment.id)} />,
                    <Popconfirm
                      title="确定删除这个环境管理项目吗？"
                      onConfirm={() => handleDeleteEnvironment(environment.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <DeleteOutlined key="delete" />
                    </Popconfirm>
                  ]}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      {getTypeIcon(environment.environment_type)}
                    </div>
                    <Title level={5} style={{ margin: '8px 0' }}>
                      {environment.name}
                    </Title>
                    <Tag color={getTypeColor(environment.environment_type)} style={{ marginBottom: '8px' }}>
                      {getTypeName(environment.environment_type)}
                    </Tag>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        📅 {environment.created_date ? dayjs(environment.created_date).format('MM-DD') : '未设置'}
                      </Text>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Tag color={getStatusColor(environment.status)}>
                        {getStatusName(environment.status)}
                      </Tag>
                    </div>
                    {environment.priority && (
                      <div style={{ marginTop: '4px' }}>
                        <Tag color={getPriorityColor(environment.priority)}>
                          {getPriorityName(environment.priority)}优先级
                        </Tag>
                      </div>
                    )}
                    {environment.details && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                        {environment.details}
                      </Text>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
            {environments.length === 0 && (
              <Col span={24}>
                <Card>
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <EnvironmentOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                    <Title level={4} style={{ color: '#D9D9D9' }}>暂无环境管理项目</Title>
                    <Text type="secondary">点击"新增环境管理"开始管理你的环境</Text>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </div>

      {/* 环境管理弹窗 */}
      {showEnvironmentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {selectedEnvironment ? '编辑' : '新增'}环境管理
              </h3>
              <Button
                type="text"
                onClick={() => setShowEnvironmentModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={environmentForm}
          layout="vertical"
          onFinish={handleSaveEnvironment}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="environment_type"
                label="环境类型"
                rules={[{ required: true, message: '请选择环境类型' }]}
              >
                <select 
                  placeholder="请选择类型"
                  value={environmentForm.getFieldValue('type') || ''}
                  onChange={(e) => environmentForm.setFieldValue('type', e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                >
                  <option value="">请选择类型</option>
                  {environmentConfig.environmentTypes.map(type => (
                    <option key={type.key} value={type.key}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="实施状态"
                rules={[{ required: true, message: '请选择实施状态' }]}
              >
                <select 
                  placeholder="请选择状态"
                  value={environmentForm.getFieldValue('status') || ''}
                  onChange={(e) => environmentForm.setFieldValue('status', e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                >
                  <option value="">请选择状态</option>
                  {environmentConfig.implementationStatus.map(status => (
                    <option key={status.key} value={status.key}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入环境管理项目名称" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="created_date"
                label="创建日期"
              >
                <Input placeholder="项目创建日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="target_date"
                label="目标日期"
              >
                <Input placeholder="预期完成日期" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="priority"
            label="优先级"
          >
            <select 
              placeholder="请选择优先级"
              value={environmentForm.getFieldValue('priority') || ''}
              onChange={(e) => environmentForm.setFieldValue('priority', e.target.value)}
              style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
            >
              <option value="">请选择优先级</option>
              {environmentConfig.priorities.map(priority => (
                <option key={priority.key} value={priority.key}>
                  {priority.name}
                </option>
              ))}
            </select>
          </Form.Item>
          
          <Form.Item
            name="details"
            label="项目详情"
          >
            <TextArea rows={3} placeholder="请详细描述环境管理项目" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowEnvironmentModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => environmentForm.submit()}>
                {selectedEnvironment ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 跟进记录弹窗 */}
      {showFollowUpModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {selectedFollowUp ? '编辑' : '新增'}跟进记录
              </h3>
              <Button
                type="text"
                onClick={() => setShowFollowUpModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={followUpForm}
          layout="vertical"
          onFinish={handleSaveFollowUp}
        >
          <Form.Item
            name="environment_id"
            label="关联项目"
            rules={[{ required: true, message: '请选择关联项目' }]}
          >
            <select 
              placeholder="请选择环境管理项目"
              value={followUpForm.getFieldValue('environment_id') || ''}
              onChange={(e) => followUpForm.setFieldValue('environment_id', e.target.value)}
              style={{ width: '100%', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
            >
              <option value="">请选择环境管理项目</option>
              {environments.map(env => (
                <option key={env.id} value={env.id}>
                  {env.name}
                </option>
              ))}
            </select>
          </Form.Item>
          
          <Form.Item
            name="follow_up_date"
            label="跟进日期"
          >
            <Input placeholder="跟进日期" />
          </Form.Item>
          
          <Form.Item
            name="content"
            label="跟进内容"
            rules={[{ required: true, message: '请输入跟进内容' }]}
          >
            <TextArea rows={3} placeholder="请输入跟进记录内容" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowFollowUpModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => followUpForm.submit()}>
                {selectedFollowUp ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 环境TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <EnvIcon style={{ color: environmentConfig.bgColor, marginRight: '8px' }} />
                环境管理TIPS
              </h3>
              <Button
                type="text"
                onClick={() => setShowTipsModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <div style={{ padding: '16px 0' }}>
          {/* 环境管理原则 */}
          <Card title="环境管理五大原则" style={{ marginBottom: '16px' }}>
            <Timeline>
              {environmentConfig.environmentManagementTips.principles.map((principle, index) => (
                <Timeline.Item key={index}>
                  <Title level={5}>
                    <span style={{ marginRight: '8px' }}>{principle.icon}</span>
                    {principle.title}
                  </Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                    {principle.description}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* 环境管理流程 */}
          <Card title="环境管理实施流程" style={{ marginBottom: '16px' }}>
            <Steps direction="vertical" size="small">
              {environmentConfig.environmentManagementProcess.steps.map((step, index) => (
                <Step
                  key={index}
                  title={
                    <div>
                      <span style={{ marginRight: '8px' }}>{step.icon}</span>
                      {step.title}
                    </div>
                  }
                  description={
                    <div>
                      <Text strong>{step.description}</Text>
                      <br />
                      <Text type="secondary">{step.details}</Text>
                    </div>
                  }
                />
              ))}
            </Steps>
          </Card>

          {/* 功能说明 */}
          <Card title="功能模块说明">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🏠 家居环境规划</Title>
                  <Text type="secondary">合理规划空间布局，优化采光通风，控制噪音和温度</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🌿 家居资源管理</Title>
                  <Text type="secondary">管理能源、水资源、材料选择，定期维护设备</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🛡️ 污染控制</Title>
                  <Text type="secondary">控制空气、噪音、光、化学污染，创造健康环境</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>⚡ 节能环保</Title>
                  <Text type="secondary">采用节能技术和环保材料，减少能源消耗</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>💨 空气质量</Title>
                  <Text type="secondary">监测和改善室内空气质量，使用净化设备</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>♻️ 废物管理</Title>
                  <Text type="secondary">垃圾分类和处理，建立循环利用机制</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
          </div>
        </div>
      )}
      </div>
    </VipRestriction>
  );
};

export default Environment;
